/*    
<summary>
  This functional component "AddUser" defines the props like initial values and functions for the child form component.
  Developer:Aashish Singh, Created Date:29-August-2023
</summary>
<returns>Returns JSX</returns>
*/

import { observer } from "mobx-react";
import React, { useEffect } from "react";
import toast from "react-hot-toast";
import { useStore } from "../../../../contexts/store-provider";
import {
  getAccessOrgIdFromLocalStorage,
  getTenantAccessFromLocalStorage,
} from "../../../../helpers/localstorage-helper";
import { formatMessage } from "../../../../translations/format-message";
import AddUpdateFreeTextSpecialSchedulerForm from "../../forms/add-edit-free-text-special-scheduler-form";
import "./add-edit-free-text-special-scheduler.css";

interface IProps {
  id: number;
  modalClosed: () => void;
}

const AddFreeTextSpecialScheduler = (props: IProps) => {
  const { preferencesStore, priceSchedulerStore, mediaStore } = useStore();
  const { getTenantAccess, getFreeTextSchedulerAccess } = preferencesStore;
  const {
    specialFreeTextSchedulerDetails,
    addUpdateSpecialSchedulerState,
    allSpecialSchedulerState,
    allSpecialSchedule,
    getSpecialSchedulerByIdState,
    AddFreeTextSepcialSchedulerService,
    addUpdateFreeTextSchedulerState,
    reset,
    GetFreeTextSchedulerServiceById,
    UpdateFreeTextWeekdaySchedulerService,
    GetAllFreeTextSchedulers,
    allFreeTextSchedulers,
    resetGetFreeTextSchedulerDetail,
  } = priceSchedulerStore;
  const { GetAllFolders, allFreeTextFolders, allAvailableFreeTexts } =
    mediaStore;
  const initialValues: any = specialFreeTextSchedulerDetails;

  /**
   * The useEffect Hook lets you perform side effects in function component.
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered
   * while a dependency of the useEffect has changed.
   */

  useEffect(() => {
    GetAllFreeTextSchedulers(getAccessOrgIdFromLocalStorage(), getFreeTextSchedulerAccess.plId); 
    GetAllFolders(getTenantAccessFromLocalStorage());
    if (props.id > 0) {
      GetFreeTextSchedulerServiceById(
        props.id,
        getAccessOrgIdFromLocalStorage(),
      );
    }
  }, []);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateUserSuccess" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateFreeTextSchedulerState.success) {
      if (props.id === -1) toast.success(formatMessage("added_success"));
      else toast.success(formatMessage("updated_success"));
      onModalClosed();
    }
  }, [addUpdateFreeTextSchedulerState.success]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateUserError" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateFreeTextSchedulerState.error) {
      toast.error(formatMessage(addUpdateFreeTextSchedulerState.error));
      reset();
    }
  }, [addUpdateFreeTextSchedulerState.error]);

  /**
   * This function adds the branch when "Id < 0" and updates the Existing branch when Id > 0 by providing form values to the
   * correponding function
   */
  const onSubmitForm = (values: any) => {
    var addObj: any = {
      OrganizationId: getAccessOrgIdFromLocalStorage()?.toLowerCase(),
      PlId: getFreeTextSchedulerAccess.plId,
      ScheduleType: Number(values.ScheduleType),
      StartTime: values.startTime,
      EndTime: values.endTime,
      StartDate: values.startDate,
      EndDate: values.endDate,
      FreeTextId1: JSON.parse(values.Data1) == -1 ? null : JSON.parse(values.Data1),
      FreeTextId2: JSON.parse(values.Data2) == -1 ? null : JSON.parse(values.Data2),
      FreeTextId3: JSON.parse(values.Data3) == -1 ? null : JSON.parse(values.Data3),
      FreeTextId4: JSON.parse(values.Data4) == -1 ? null : JSON.parse(values.Data4),
      FreeTextId5: JSON.parse(values.Data5) == -1 ? null : JSON.parse(values.Data5),
      FreeTextId6: JSON.parse(values.Data6) == -1 ? null : JSON.parse(values.Data6),
      Weekday: null,
      FolderId: JSON.parse(values.FolderId),
    };

    var updateObj: any = {
      FtsId: props.id,
      PlId: getFreeTextSchedulerAccess.plId,
      ScheduleType: values.ScheduleType,
      Weekday: null,
      StartDate: values.startDate,
      EndDate: values.endDate,
      StartTime: values.startTime,
      EndTime: values.endTime,
      FolderId: JSON.parse(values.FolderId),
      FreeTextId1: JSON.parse(values.Data1) == -1 ? null : JSON.parse(values.Data1),
      FreeTextId2: JSON.parse(values.Data2) == -1 ? null : JSON.parse(values.Data2),
      FreeTextId3: JSON.parse(values.Data3) == -1 ? null : JSON.parse(values.Data3),
      FreeTextId4: JSON.parse(values.Data4) == -1 ? null : JSON.parse(values.Data4),
      FreeTextId5: JSON.parse(values.Data5) == -1 ? null : JSON.parse(values.Data5),
      FreeTextId6: JSON.parse(values.Data6) == -1 ? null : JSON.parse(values.Data6),
      OrganizationId: getAccessOrgIdFromLocalStorage().toLowerCase(),
    };
    if (props.id < 0) AddFreeTextSepcialSchedulerService(addObj);
    // else UpdateSpecialSchedulerService(updateObj);
    else UpdateFreeTextWeekdaySchedulerService(updateObj);
  };

  /**
   * This function closes the pop up modal and reset getbrachDetails, addUpdate branch observables (success, inprogress
   * and error).
   */
  const onModalClosed = () => {
    resetGetFreeTextSchedulerDetail();
    props.modalClosed();
  };

  return (
    <React.Fragment>
      <AddUpdateFreeTextSpecialSchedulerForm
        initialValues={initialValues}
        allSpecialSchedules={allFreeTextSchedulers}
        id={props.id}
        submitHandler={onSubmitForm}
        onModalClosed={onModalClosed}
        allFreeTextFolders={allFreeTextFolders}
        allAvailableImages={allAvailableFreeTexts}
        isLoading={
          addUpdateSpecialSchedulerState.inProgress ||
          getSpecialSchedulerByIdState.inProgress ||
          allSpecialSchedulerState.inProgress
        }
      />
    </React.Fragment>
  );
};

export default observer(AddFreeTextSpecialScheduler);
